<script lang="ts" setup>
import type Housing from "@/models/Housing";
import type HousingCard from "@/models/HousingCard";

const emit = defineEmits<{
  (event: "update:page", page: number): void;
  (event: "update:order", key: string): void;
  (event: "update-watching", value: { housingId: number; watched: null | string }): void;
  (event: "trigger:toggleMobileFilter"): void;
}>();

const props = withDefaults(
  defineProps<{
    searchCards: HousingCard[];
    orderOptions?: string[];
    housingsPerPage?: number;
    searchCount?: number;
    page?: number;
    pageCount?: number;
    order?: string;
    showHidden?: boolean;
    showOrderBy?: boolean;
    showPagination?: boolean;
  }>(),
  {
    orderOptions: () => ["standard", "date_desc", "rent_asc", "rent_desc", "rooms_asc", "rooms_desc", "size_asc", "size_desc"],
    headlineLevel: 3,
    housingsPerPage: 10,
    searchCount: 0,
    page: 1,
    pageCount: 0,
    order: "date_desc",
    showHidden: false,
    showOrderBy: true,
    showPagination: true,
  }
);

const isDesktop = useIsDesktop();
const reportingHousing = ref<Housing | null>(null);
const {
  successStoryResult,
  successStoryPosition,
  registerNowResult,
  registerNowPosition,
  discountResult,
  discountPosition,
  multiPortalResult,
  multiPortalPosition,
  adjustSearchFilterResult,
  adjustSearchFilterPosition,
  recalculateShoutOuts,
} = useShoutOuts();

const hydrated = ref(false);

onMounted(() => {
  hydrated.value = true;
});

watch(
  () => props.page,
  () => {
    recalculateShoutOuts();
  }
);
</script>

<template>
  <div class="tw-housing-card-list">
    <header>
      <div v-if="isDesktop" class="count">
        <slot name="count" />
      </div>
      <template v-if="props.showOrderBy">
        <tw-ui-order-modal v-if="!isDesktop" :options="props.orderOptions" :value="props.order" @update:value="emit('update:order', $event)" />
        <tw-ui-order-dropdown v-else :options="props.orderOptions" :value="props.order" @update:value="emit('update:order', $event)" />
      </template>
    </header>

    <ul v-if="props.searchCards.length">
      <li v-for="(searchCard, idx) in props.searchCards" :key="`HousingCardList_HousingCard_${searchCard.id}`">
        <tw-search-card
          :search-card="searchCard"
          :show-hidden="props.showHidden"
          @report="reportingHousing = searchCard.housing"
          @update-watching="emit('update-watching', $event)"
          :card-index="idx"
        />
        <tw-shout-outs
          v-if="hydrated"
          :idx="idx"
          :page="props.page"
          :success-story-result="successStoryResult"
          :success-story-position="successStoryPosition"
          :register-now-result="registerNowResult"
          :register-now-position="registerNowPosition"
          :discount-result="discountResult"
          :discount-position="discountPosition"
          :multi-portal-result="multiPortalResult"
          :multi-portal-position="multiPortalPosition"
          :adjust-search-filter-result="adjustSearchFilterResult"
          :adjust-search-filter-position="adjustSearchFilterPosition"
          @trigger:toggle-mobile-filter="emit('trigger:toggleMobileFilter')"
        />
      </li>
    </ul>

    <footer>
      <tw-ui-pagination
        v-if="props.showPagination && props.pageCount > 1"
        :max="props.pageCount"
        :show-next="true"
        :show-previous="true"
        :value="props.page"
        @update:value="emit('update:page', $event)"
      />
    </footer>

    <tw-report-housing-card-modal v-if="!!reportingHousing" :housing="reportingHousing" :state="!!reportingHousing" @close="reportingHousing = null" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/tw-variables.scss";

.tw-housing-card-list {
  display: flex;
  flex-direction: column;
  align-items: center;

  > header {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  > ul {
    list-style-type: none;
    padding: 0;
    width: 100%;

    li {
      margin: 1em 0;
    }
  }

  > footer {
    width: 100%;
  }
}

:deep(.icon--search) {
  height: 1em;
}
</style>
